import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTwitter } from '@soundxyz/font-awesome/free-brands-svg-icons/index.mjs';
import type { Maybe } from '@soundxyz/utils';
import type { PostModalSource } from '@soundxyz/utils/analytics';
import { Button } from '@components/main-components/button/Button';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ModalContext, ModalType } from '@context/modal';
import type { FeedPostModalMainSoundFragment } from '@graphql/reactQuery';
import { useBackendUserPersistedData } from '@hooks/useBackendUserPersistedData';
import { FeedPostModal } from '.';

const UNLOCK_TWEET_TEXT =
  'Unlocking the feed on @soundxyz_. 🎧 Find & follow me to view my posts \n\n https://www.sound.xyz/feed \n #soundcurator';

export const UnlockFeedModal = ({
  sourceLocation,
  releaseFrag,
}: {
  sourceLocation: PostModalSource;
  releaseFrag?: Maybe<FeedPostModalMainSoundFragment>;
}) => {
  const { closeCancelModal, openModal } = ModalContext.useContainer();
  const { setHasFeedAccess } = useBackendUserPersistedData();
  const body = (
    <div className="flex flex-col items-center gap-4">
      <NextFutureImage
        src="https://d16zqvkniqf1rn.cloudfront.net/lock_hologram.png"
        width={50}
        height={50}
        sizes="60px"
        quality={80}
        alt="Unlock the feed"
      />
      <div className="flex flex-col items-center justify-center gap-2">
        <h2 className="font-title text-title-m font-medium md:text-title-l">Unlock the feed</h2>
        <p className="text-center font-base text-base-l">
          Share to Twitter to unlock
          <br />
          posting to the feed.
        </p>
      </div>
    </div>
  );
  return (
    <StandardModal
      body={body}
      closeModal={closeCancelModal}
      callToAction={
        <a
          className="w-full"
          target="_blank"
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(UNLOCK_TWEET_TEXT)}`}
          rel="noreferrer"
          onClick={() => {
            setHasFeedAccess(true);

            setTimeout(() => {
              closeCancelModal();
              openModal(ModalType.FEED_POST, {
                body: (
                  <FeedPostModal
                    key="post-input"
                    releaseFrag={releaseFrag}
                    sourceLocation={sourceLocation}
                  />
                ),
              });
            }, 1000);
          }}
        >
          <Button
            variant="primary"
            label="Tweet to unlock"
            leadingIcon={<FontAwesomeIcon icon={faTwitter} />}
            fullWidth
            size="L"
          />
        </a>
      }
    />
  );
};
